import { View, Pressable, StyleSheet } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import { useTheme } from "@/hooks/useTheme";
import type { SelectionConfig, ActionBarAction } from "./types";
import { HEADER } from "@/components/Header/constants";

type SelectionZoneProps = {
  config: SelectionConfig;
};

export function SelectionZone({ config }: SelectionZoneProps) {
  const { colors } = useTheme();
  const hasSelection = config.count > 0;

  const renderButton = (action: ActionBarAction, selectionDisabled: boolean) => {
    const isPrimary = action.variant === "primary";
    const isDanger = action.variant === "danger";
    const disabled = selectionDisabled || action.disabled;

    let bg = "transparent";
    let fg = colors.text;
    let border = colors.border;

    if (disabled) {
      bg = "transparent";
      fg = colors.textSecondary;
      border = colors.border;
    } else if (isPrimary) {
      bg = colors.green?.background || "#dcfce7";
      fg = colors.green?.text || "#16a34a";
      border = colors.green?.border || "#bbf7d0";
    } else if (isDanger) {
      bg = colors.red?.background || "#fef2f2";
      fg = colors.red?.text || "#dc2626";
      border = colors.red?.border || "#fecaca";
    }

    return (
      <Button
        key={action.id}
        text={action.label}
        icon={action.icon}
        iconSize={15}
        disabled={disabled}
        onPress={action.onPress}
        style={[styles.actionBtn, { backgroundColor: bg, borderColor: border }, disabled && { opacity: 0.5 }]}
        textStyle={{ fontSize: 12, fontWeight: "600", color: fg }}
        iconStyle={{ color: fg }}
      />
    );
  };

  return (
    <View style={styles.container}>
      {/* Selection indicator */}
      <View
        style={[
          styles.indicator,
          {
            backgroundColor: hasSelection ? colors.blue?.background || "#eff6ff" : colors.backgroundSecondary,
            borderColor: hasSelection ? colors.blue?.border || "#bfdbfe" : colors.border,
          },
        ]}
      >
        <MaterialCommunityIcons
          name={hasSelection ? "checkbox-marked-circle-outline" : "checkbox-blank-circle-outline"}
          size={14}
          color={hasSelection ? colors.blue?.text || "#2563eb" : colors.textSecondary}
        />
        <ThemedText
          style={[
            styles.indicatorText,
            { color: hasSelection ? colors.blue?.text || "#2563eb" : colors.textSecondary },
          ]}
        >
          {hasSelection ? `${config.count} selected` : "None selected"}
        </ThemedText>
      </View>

      {/* Selection actions (always visible, disabled when no selection) */}
      {config.actions?.map((action) => renderButton(action, !hasSelection))}

      {/* Clear selection button */}
      <Pressable
        onPress={config.onClear}
        disabled={!hasSelection}
        style={({ hovered }: any) => [
          styles.clearBtn,
          { borderColor: colors.border },
          hovered && hasSelection && { backgroundColor: colors.backgroundSecondary },
          !hasSelection && { opacity: 0.5 },
        ]}
      >
        <MaterialCommunityIcons name="close" size={14} color={colors.textSecondary} />
      </Pressable>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  indicator: {
    flexDirection: "row",
    alignItems: "center",
    gap: 5,
    paddingHorizontal: 8,
    height: HEADER.HEIGHT,
    borderRadius: HEADER.RADIUS,
    borderWidth: StyleSheet.hairlineWidth,
  },
  indicatorText: {
    fontSize: 12,
    fontWeight: "600",
  },
  actionBtn: {
    height: HEADER.HEIGHT,
    paddingHorizontal: 8,
    borderRadius: HEADER.RADIUS,
    borderWidth: StyleSheet.hairlineWidth,
    gap: 4,
  },
  clearBtn: {
    width: HEADER.HEIGHT - 4,
    height: HEADER.HEIGHT - 4,
    borderRadius: HEADER.RADIUS,
    borderWidth: StyleSheet.hairlineWidth,
    alignItems: "center",
    justifyContent: "center",
  },
});
